/**
 * Encryption Service - Secure credential storage with Web Crypto API
 *
 * Security features:
 * - AES-GCM 256-bit encryption
 * - PBKDF2 key derivation with 600,000 iterations (OWASP 2023 recommendation)
 * - PIN-based master key derivation option
 * - Random salt and IV for each encryption
 * - IndexedDB for encrypted storage (more secure than localStorage)
 * - Session-based key caching with auto-lock
 * - Automatic migration from plain-text localStorage
 *
 * Usage:
 *   await encryptionService.init();
 *   // Optional: Setup PIN-based encryption
 *   await encryptionService.setupPinEncryption('123456');
 *   await encryptionService.unlockWithPin('123456');
 *   await encryptionService.setCredentials('kucoin', credentials);
 *   const creds = await encryptionService.getCredentials('kucoin');
 */


// =====================================================
// TYPES
// =====================================================

interface EncryptedData {
  encryptedData: string; // Base64 encoded
  iv: string; // Base64 encoded Initialization Vector
  salt: string; // Base64 encoded salt
  timestamp: string;
  version: number; // Encryption version for future migrations
}

interface PinConfig {
  salt: string; // Base64 encoded salt for PIN derivation
  verificationHash: string; // Hash to verify PIN is correct
  createdAt: string;
  version: number;
}

export interface EncryptionStatus {
  initialized: boolean;
  locked: boolean;
  pinConfigured: boolean;
  lastActivity: number;
}

type LockCallback = (locked: boolean) => void;

// =====================================================
// CONSTANTS
// =====================================================

const DB_NAME = 'SecureCredentialsDB';
const DB_VERSION = 2; // Incremented for schema changes
const CREDENTIALS_STORE = 'credentials';
const CONFIG_STORE = 'config';

// OWASP 2023 recommendation for PBKDF2 with SHA-256
const PBKDF2_ITERATIONS = 600000;
const AUTO_LOCK_MS = 5 * 60 * 1000; // Auto-lock after 5 minutes of inactivity
const ENCRYPTION_VERSION = 2;

const PIN_CONFIG_KEY = 'pin_config';
const VERIFICATION_TEXT = 'WealthHub_PIN_Verification_2024';

// =====================================================
// ENCRYPTION SERVICE
// =====================================================

class EncryptionService {
  private db: IDBDatabase | null = null;
  private masterKey: CryptoKey | null = null;
  private lastActivity: number = 0;
  private autoLockTimer: ReturnType<typeof setTimeout> | null = null;
  private lockCallbacks: Set<LockCallback> = new Set();
  private _initialized = false;
  private _pinConfigured = false;

  // =====================================================
  // INITIALIZATION
  // =====================================================

  /**
   * Initialize the encryption service and IndexedDB
   */
  async init(): Promise<boolean> {
    if (this._initialized) {
      return true;
    }

    try {
      await this.openDatabase();

      // Check if PIN is configured
      this._pinConfigured = await this.hasPinConfigured();

      // If no PIN configured, use device-based encryption (legacy mode)
      if (!this._pinConfigured) {
        await this.initializeLegacyKey();
      }

      this._initialized = true;
      return true;
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('EncryptionService: Failed to initialize:', error);
      }
      return false;
    }
  }

  /**
   * Open IndexedDB connection with schema migration
   */
  private openDatabase(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        reject(new Error('Failed to open IndexedDB'));
      };

      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        const oldVersion = event.oldVersion;

        // Create credentials store if needed
        if (!db.objectStoreNames.contains(CREDENTIALS_STORE)) {
          db.createObjectStore(CREDENTIALS_STORE, { keyPath: 'key' });
        }

        // Create config store for PIN settings (new in v2)
        if (oldVersion < 2 && !db.objectStoreNames.contains(CONFIG_STORE)) {
          db.createObjectStore(CONFIG_STORE, { keyPath: 'key' });
        }
      };
    });
  }

  // =====================================================
  // PIN-BASED ENCRYPTION
  // =====================================================

  /**
   * Check if PIN-based encryption is configured
   */
  async hasPinConfigured(): Promise<boolean> {
    if (!this.db) return false;

    try {
      const config = await this.getFromStore<{ key: string; value: PinConfig }>(CONFIG_STORE, PIN_CONFIG_KEY);
      return config !== null;
    } catch {
      return false;
    }
  }

  /**
   * Setup PIN-based encryption
   */
  async setupPinEncryption(pin: string): Promise<boolean> {
    if (!this.db) {
      throw new Error('Database not initialized');
    }

    if (pin.length < 4) {
      throw new Error('PIN must be at least 4 characters');
    }

    try {
      // Generate salt for PIN derivation
      const salt = crypto.getRandomValues(new Uint8Array(32));

      // Derive key from PIN
      const keyMaterial = await this.pinToKeyMaterial(pin);
      const derivedKey = await this.deriveKey(keyMaterial, salt);

      // Create verification hash
      const verificationHash = await this.createVerificationHash(derivedKey);

      // Store PIN config
      const pinConfig: PinConfig = {
        salt: this.uint8ArrayToBase64(salt),
        verificationHash,
        createdAt: new Date().toISOString(),
        version: 1,
      };

      await this.putToStore(CONFIG_STORE, { key: PIN_CONFIG_KEY, value: pinConfig });

      // Set as active key
      this.masterKey = derivedKey;
      this._pinConfigured = true;
      this.updateActivity();
      this.startAutoLockTimer();

      return true;
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('EncryptionService: Failed to setup PIN:', error);
      }
      return false;
    }
  }

  /**
   * Unlock encryption with PIN
   */
  async unlockWithPin(pin: string): Promise<boolean> {
    if (!this.db) {
      throw new Error('Database not initialized');
    }

    try {
      const configData = await this.getFromStore<{ key: string; value: PinConfig }>(CONFIG_STORE, PIN_CONFIG_KEY);
      if (!configData) {
        throw new Error('PIN not configured');
      }

      const config = configData.value;
      const salt = this.base64ToUint8Array(config.salt);

      // Derive key from PIN
      const keyMaterial = await this.pinToKeyMaterial(pin);
      const derivedKey = await this.deriveKey(keyMaterial, salt);

      // Verify PIN
      const verificationHash = await this.createVerificationHash(derivedKey);

      if (verificationHash !== config.verificationHash) {
        return false; // Wrong PIN
      }

      // PIN correct - set as active key
      this.masterKey = derivedKey;
      this.updateActivity();
      this.startAutoLockTimer();
      this.notifyLockCallbacks(false);

      return true;
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('EncryptionService: Failed to unlock:', error);
      }
      return false;
    }
  }

  /**
   * Lock the encryption service (clear key from memory)
   */
  lock(): void {
    this.masterKey = null;
    this.stopAutoLockTimer();
    this.notifyLockCallbacks(true);
  }

  /**
   * Check if service is locked
   */
  isLocked(): boolean {
    return this.masterKey === null && this._pinConfigured;
  }

  /**
   * Get current status
   */
  getStatus(): EncryptionStatus {
    return {
      initialized: this._initialized,
      locked: this.isLocked(),
      pinConfigured: this._pinConfigured,
      lastActivity: this.lastActivity,
    };
  }

  /**
   * Register callback for lock state changes
   */
  onLockChange(callback: LockCallback): () => void {
    this.lockCallbacks.add(callback);
    return () => this.lockCallbacks.delete(callback);
  }

  // =====================================================
  // LEGACY (DEVICE-BASED) ENCRYPTION
  // =====================================================

  /**
   * Initialize legacy device-based encryption key
   */
  private async initializeLegacyKey(): Promise<void> {
    const MASTER_KEY_STORAGE = 'encryption_master_key_salt';
    const savedSalt = localStorage.getItem(MASTER_KEY_STORAGE);

    let salt: Uint8Array;
    if (savedSalt) {
      salt = this.base64ToUint8Array(savedSalt);
    } else {
      salt = crypto.getRandomValues(new Uint8Array(32));
      localStorage.setItem(MASTER_KEY_STORAGE, this.uint8ArrayToBase64(salt));
    }

    // Generate deterministic key material from device info
    const deviceInfo = navigator.userAgent + navigator.language + (navigator.hardwareConcurrency || '');
    const encoder = new TextEncoder();
    const keyData = encoder.encode(deviceInfo);

    const keyMaterial = await crypto.subtle.importKey(
      'raw',
      keyData,
      'PBKDF2',
      false,
      ['deriveBits', 'deriveKey']
    );

    this.masterKey = await this.deriveKey(keyMaterial, salt);
  }

  // =====================================================
  // KEY DERIVATION
  // =====================================================

  /**
   * Convert PIN to key material
   */
  private async pinToKeyMaterial(pin: string): Promise<CryptoKey> {
    const encoder = new TextEncoder();
    const pinData = encoder.encode(pin);

    return crypto.subtle.importKey(
      'raw',
      pinData,
      'PBKDF2',
      false,
      ['deriveBits', 'deriveKey']
    );
  }

  /**
   * Derive encryption key using PBKDF2 with 600,000 iterations
   */
  private async deriveKey(keyMaterial: CryptoKey, salt: Uint8Array): Promise<CryptoKey> {
    return crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt,
        iterations: PBKDF2_ITERATIONS,
        hash: 'SHA-256',
      },
      keyMaterial,
      { name: 'AES-GCM', length: 256 },
      false,
      ['encrypt', 'decrypt']
    );
  }

  /**
   * Create verification hash to check PIN correctness
   */
  private async createVerificationHash(key: CryptoKey): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(VERIFICATION_TEXT);
    const iv = new Uint8Array(12); // Fixed IV for verification only

    const encrypted = await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv },
      key,
      data
    );

    return this.arrayBufferToBase64(encrypted);
  }

  // =====================================================
  // ENCRYPTION / DECRYPTION
  // =====================================================

  /**
   * Encrypt data using AES-GCM
   */
  private async encrypt(data: unknown): Promise<EncryptedData> {
    if (!this.masterKey) {
      throw new Error('Encryption key not available. Please unlock first.');
    }

    const iv = crypto.getRandomValues(new Uint8Array(12));
    const salt = crypto.getRandomValues(new Uint8Array(32));
    const encoder = new TextEncoder();
    const dataBuffer = encoder.encode(JSON.stringify(data));

    const encryptedBuffer = await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv },
      this.masterKey,
      dataBuffer
    );

    this.updateActivity();

    return {
      encryptedData: this.arrayBufferToBase64(encryptedBuffer),
      iv: this.uint8ArrayToBase64(iv),
      salt: this.uint8ArrayToBase64(salt),
      timestamp: new Date().toISOString(),
      version: ENCRYPTION_VERSION,
    };
  }

  /**
   * Decrypt data using AES-GCM
   */
  private async decrypt(encryptedData: EncryptedData): Promise<unknown> {
    if (!this.masterKey) {
      throw new Error('Encryption key not available. Please unlock first.');
    }

    try {
      const encryptedBuffer = this.base64ToArrayBuffer(encryptedData.encryptedData);
      const iv = this.base64ToUint8Array(encryptedData.iv);

      const decryptedBuffer = await crypto.subtle.decrypt(
        { name: 'AES-GCM', iv },
        this.masterKey,
        encryptedBuffer
      );

      const decoder = new TextDecoder();
      const decryptedString = decoder.decode(decryptedBuffer);

      this.updateActivity();
      return JSON.parse(decryptedString);
    } catch (error) {
      throw new Error('Failed to decrypt data. The data may be corrupted or the key is incorrect.');
    }
  }

  // =====================================================
  // CREDENTIAL STORAGE
  // =====================================================

  /**
   * Store encrypted credentials in IndexedDB
   */
  async setCredentials(key: string, credentials: unknown): Promise<void> {
    if (!this.db) {
      throw new Error('Database not initialized');
    }

    const encryptedData = await this.encrypt(credentials);
    await this.putToStore(CREDENTIALS_STORE, { key, value: encryptedData });

    // Remove from localStorage if it exists (migration)
    localStorage.removeItem(key);

  }

  /**
   * Retrieve and decrypt credentials from IndexedDB
   */
  async getCredentials<T = unknown>(key: string): Promise<T | null> {
    if (!this.db) {
      throw new Error('Database not initialized');
    }

    try {
      const result = await this.getFromStore<{ key: string; value: EncryptedData }>(CREDENTIALS_STORE, key);

      if (!result) {
        return await this.migrateFromLocalStorage<T>(key);
      }

      const decrypted = await this.decrypt(result.value);
      return decrypted as T;
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error(`EncryptionService: Failed to retrieve ${key}:`, error);
      }
      return null;
    }
  }

  /**
   * Remove credentials
   */
  async removeCredentials(key: string): Promise<void> {
    if (!this.db) {
      throw new Error('Database not initialized');
    }

    await this.deleteFromStore(CREDENTIALS_STORE, key);
    localStorage.removeItem(key);
  }

  /**
   * Check if credentials exist
   */
  async hasCredentials(key: string): Promise<boolean> {
    const creds = await this.getCredentials(key);
    return creds !== null;
  }

  /**
   * Migrate credentials from plain-text localStorage
   */
  private async migrateFromLocalStorage<T>(key: string): Promise<T | null> {
    try {
      const plainData = localStorage.getItem(key);
      if (!plainData) {
        return null;
      }

      const parsedData = JSON.parse(plainData);
      await this.setCredentials(key, parsedData);

      return parsedData as T;
    } catch {
      return null;
    }
  }

  /**
   * Migrate all known credentials from localStorage
   */
  async migrateAllCredentials(): Promise<void> {
    const credentialKeys = [
      'kucoin_credentials',
      'cryptocom_credentials',
      'biometric_credential',
      'biometric_lock_on_start',
    ];

    for (const key of credentialKeys) {
      const plainData = localStorage.getItem(key);
      if (plainData) {
        try {
          const parsedData = JSON.parse(plainData);
          await this.setCredentials(key, parsedData);
        } catch {
          // Skip invalid data
        }
      }
    }

  }

  // =====================================================
  // AUTO-LOCK
  // =====================================================

  private updateActivity(): void {
    this.lastActivity = Date.now();
  }

  private startAutoLockTimer(): void {
    this.stopAutoLockTimer();

    this.autoLockTimer = setTimeout(() => {
      if (Date.now() - this.lastActivity >= AUTO_LOCK_MS) {
        this.lock();
      } else {
        this.startAutoLockTimer();
      }
    }, AUTO_LOCK_MS);
  }

  private stopAutoLockTimer(): void {
    if (this.autoLockTimer) {
      clearTimeout(this.autoLockTimer);
      this.autoLockTimer = null;
    }
  }

  private notifyLockCallbacks(locked: boolean): void {
    this.lockCallbacks.forEach(callback => callback(locked));
  }

  // =====================================================
  // INDEXEDDB HELPERS
  // =====================================================

  private getFromStore<T>(storeName: string, key: string): Promise<T | null> {
    return new Promise((resolve, reject) => {
      if (!this.db) {
        reject(new Error('Database not initialized'));
        return;
      }

      const transaction = this.db.transaction([storeName], 'readonly');
      const store = transaction.objectStore(storeName);
      const request = store.get(key);

      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error);
    });
  }

  private putToStore(storeName: string, data: { key: string; value: unknown }): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.db) {
        reject(new Error('Database not initialized'));
        return;
      }

      const transaction = this.db.transaction([storeName], 'readwrite');
      const store = transaction.objectStore(storeName);
      const request = store.put(data);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  private deleteFromStore(storeName: string, key: string): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.db) {
        reject(new Error('Database not initialized'));
        return;
      }

      const transaction = this.db.transaction([storeName], 'readwrite');
      const store = transaction.objectStore(storeName);
      const request = store.delete(key);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  // =====================================================
  // BASE64 HELPERS
  // =====================================================

  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }

  private base64ToArrayBuffer(base64: string): ArrayBuffer {
    const binaryString = atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
  }

  private uint8ArrayToBase64(array: Uint8Array): string {
    return this.arrayBufferToBase64(array.buffer);
  }

  private base64ToUint8Array(base64: string): Uint8Array {
    const buffer = this.base64ToArrayBuffer(base64);
    return new Uint8Array(buffer);
  }
}

// Singleton instance
export const encryptionService = new EncryptionService();

// Auto-initialize on module load
if (typeof window !== 'undefined') {
  encryptionService.init().then(async (success) => {
    if (success) {
      await encryptionService.migrateAllCredentials();
    }
  });
}
